import { Box, SimpleGrid, useMediaQuery, useToast } from '@chakra-ui/react';
import type {
  CartRecommendationCartProduct,
  CartRecommendationDisplayContext,
  CartRecommendationItem,
} from '@diamond/shared/types';
import { showToast, Text } from '@diamond/shared/ui';
import {
  apiInstance,
  buyerCaptureException,
  LIST_FEATURE_FLAGS,
  trackEvent,
  useFeatureFlag,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { buildRecommendationEcommerceItems } from '@diamond/sol-buyer/utils';
import {
  CART_TOTAL_QUERY_KEY,
  useAuthStore,
  useCartRecommendations,
  useShipToStore,
} from '@diamond/sol-buyer-context';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CartRecommendationCard } from './CartRecommendationCard';

// const RECOMMENDATION_TITLE_CART_PREFIX = 'Rekomendasi Sesuai';
// const RECOMMENDATION_TITLE_CART_SUFFIX = 'Anda';
const RECOMMENDATION_TITLE_POPUP = 'Rekomendasi Sesuai Bisnis';
const MAX_RECOMMENDATIONS = 8;

const ITEM_LIST_NAME_BY_CONTEXT: Record<
  CartRecommendationDisplayContext,
  string
> = {
  cart_page: 'cart_recommendation_cart_page',
  add_to_cart_success_popup: 'cart_recommendation_atc_popup',
};

// FBT-specific section_name per QA feedback — distinct from the internal
// item_list_name/id used above.
const FBT_SECTION_NAME_BY_CONTEXT: Record<
  CartRecommendationDisplayContext,
  string
> = {
  cart_page: 'FBT Keranjang',
  add_to_cart_success_popup: 'FBT Popup',
};

// Event-level ui_item_* mirrors the FBT recommended product so GA can filter
// without drilling into the items[] array (same shape as upsell ui_item_*).
function buildFbtUiItemParams(item: CartRecommendationItem) {
  return {
    ui_item_id: item.product.item_code,
    ui_item_name: item.product.title,
    ui_item_brand: item.product.brand_name,
  };
}

// Only fired when the recommended item itself has an active discount promo.
function buildFbtPromotionParams(item: CartRecommendationItem) {
  const mechanism = item.product.discount_promos?.[0]?.mechanism;
  if (!mechanism) return null;

  return {
    promotion_id: `Promo - ${item.product.item_code} | ${mechanism}`,
    promotion_name: `Promo - ${mechanism}`,
  };
}

export interface CartRecommendationModuleProps {
  displayContext: CartRecommendationDisplayContext;
  selectedProductItemCodes: number[];
  cartItems: CartRecommendationCartProduct[];
  maxItems?: number;
  /**
   * Page the add-to-cart success popup was opened from (Beranda, Product
   * Detail, etc). Ignored for cart_page, which always uses "Keranjang".
   */
  originPageName?: string;
  /** Name of the just-added product, for added_to_cart_items ("code | name"). */
  addedItemName?: string;
}

export function CartRecommendationModule({
  displayContext,
  selectedProductItemCodes,
  cartItems,
  maxItems,
  originPageName,
  addedItemName,
}: CartRecommendationModuleProps) {
  const isEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_CART_RECOMMENDATION,
    false
  );

  if (!isEnabled) return null;

  return (
    <CartRecommendationModuleContent
      displayContext={displayContext}
      selectedProductItemCodes={selectedProductItemCodes}
      cartItems={cartItems}
      maxItems={maxItems}
      originPageName={originPageName}
      addedItemName={addedItemName}
    />
  );
}

function CartRecommendationModuleContent({
  displayContext,
  selectedProductItemCodes,
  cartItems,
  maxItems = MAX_RECOMMENDATIONS,
  originPageName,
  addedItemName,
}: CartRecommendationModuleProps) {
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const toast = useToast();
  const queryClient = useQueryClient();
  const token = useAuthStore((state) => state.access_token);
  const warehouseId = useShipToStore((state) => state.selectedShipToId);

  const { recommendations, isError } = useCartRecommendations(
    displayContext,
    selectedProductItemCodes,
    cartItems
  );

  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [addingId, setAddingId] = useState<string | null>(null);

  const itemListName = ITEM_LIST_NAME_BY_CONTEXT[displayContext];
  const isCartPage = displayContext === 'cart_page';
  const fbtSectionName = FBT_SECTION_NAME_BY_CONTEXT[displayContext];
  const fbtPageName = isCartPage
    ? TRACKING_PAGES.CART
    : originPageName || fbtSectionName;

  // FBT: "item_code | product_name" of the just-added product. Omitted on
  // the cart page (redundant — the page already shows the cart contents).
  const addedToCartItems = useMemo(() => {
    if (isCartPage) return undefined;
    const [code] = selectedProductItemCodes;
    if (code == null) return undefined;
    return [`${code} | ${addedItemName ?? '-'}`];
  }, [isCartPage, selectedProductItemCodes, addedItemName]);

  const visibleRecommendations = useMemo(
    () =>
      recommendations
        .filter((item) => !removedIds.has(item.recommendation.id))
        .slice(0, maxItems),
    [recommendations, removedIds, maxItems]
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const impressionTracked = useRef(false);

  useEffect(() => {
    if (impressionTracked.current) return;
    if (visibleRecommendations.length === 0) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !impressionTracked.current) {
            impressionTracked.current = true;
            const fbtItems = buildRecommendationEcommerceItems(
              visibleRecommendations,
              itemListName
            );
            trackEvent(TRACKING_EVENTS.VIEW_ITEM_LIST, {
              item_list_id: 'cart_recommendation',
              item_list_name: itemListName,
              items: fbtItems,
              section_name: fbtSectionName,
              page_name: fbtPageName,
            });
            visibleRecommendations.forEach((item) => {
              const singleFbtItem = buildRecommendationEcommerceItems(
                [item],
                itemListName
              );
              trackEvent(TRACKING_EVENTS.VIEW_ITEM_FBT, {
                ...buildFbtUiItemParams(item),
                item_list_id: 'cart_recommendation',
                item_list_name: itemListName,
                items: singleFbtItem,
                ...(addedToCartItems
                  ? { added_to_cart_items: addedToCartItems }
                  : {}),
                section_name: fbtSectionName,
                page_name: fbtPageName,
              });
              const fbtPromotionParams = buildFbtPromotionParams(item);
              if (fbtPromotionParams) {
                trackEvent(TRACKING_EVENTS.VIEW_PROMOTION, {
                  ...fbtPromotionParams,
                  items: singleFbtItem,
                  section_name: fbtSectionName,
                  page_name: fbtPageName,
                });
              }
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [
    visibleRecommendations,
    itemListName,
    addedToCartItems,
    fbtSectionName,
    fbtPageName,
  ]);

  const handleSelect = (item: CartRecommendationItem) => {
    const fbtItems = buildRecommendationEcommerceItems([item], itemListName);
    trackEvent(TRACKING_EVENTS.SELECT_ITEM, {
      item_list_id: 'cart_recommendation',
      item_list_name: itemListName,
      items: fbtItems,
    });
    trackEvent(TRACKING_EVENTS.SELECT_ITEM_FBT, {
      ...buildFbtUiItemParams(item),
      item_list_id: 'cart_recommendation',
      item_list_name: itemListName,
      items: fbtItems,
      ...(addedToCartItems ? { added_to_cart_items: addedToCartItems } : {}),
      section_name: fbtSectionName,
      page_name: fbtPageName,
    });
    const fbtPromotionParams = buildFbtPromotionParams(item);
    if (fbtPromotionParams) {
      trackEvent(TRACKING_EVENTS.SELECT_PROMOTION, {
        ...fbtPromotionParams,
        items: fbtItems,
        section_name: fbtSectionName,
        page_name: fbtPageName,
      });
    }
    const productUrl = warehouseId
      ? `/product/${item.product.id}?warehouse=${warehouseId}`
      : `/product/${item.product.id}`;
    window.open(productUrl, '_blank', 'noopener,noreferrer');
  };

  const handleAdd = async (item: CartRecommendationItem) => {
    const { product } = item;
    const quantity =
      product.minimum_order_quantity ||
      product.prices?.[0]?.min_order_quantity ||
      1000;

    setAddingId(item.recommendation.id);
    try {
      await apiInstance(token).post('/cart', {
        product_id: product.id,
        warehouse_id: warehouseId,
        quantity,
        note: null,
      });

      const fbtItems = buildRecommendationEcommerceItems([item], itemListName);
      const fbtValue = (product.prices?.[0]?.price ?? 0) * (quantity / 1000);
      trackEvent(TRACKING_EVENTS.ADD_TO_CART, {
        currency: 'IDR',
        value: fbtValue,
        items: fbtItems,
        section_name: fbtSectionName,
        page_name: fbtPageName,
      });
      trackEvent(TRACKING_EVENTS.ADD_TO_CART_FBT, {
        ...buildFbtUiItemParams(item),
        currency: 'IDR',
        value: fbtValue,
        items: fbtItems,
        ...(addedToCartItems ? { added_to_cart_items: addedToCartItems } : {}),
        section_name: fbtSectionName,
        page_name: fbtPageName,
      });

      setRemovedIds((prev) => {
        const next = new Set(prev);
        next.add(item.recommendation.id);
        return next;
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['Cart'],
          exact: false,
          refetchType: 'active',
        }),
        queryClient.invalidateQueries({
          queryKey: [CART_TOTAL_QUERY_KEY],
          exact: false,
          refetchType: 'active',
        }),
      ]);
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      const message =
        axiosError?.response?.data?.message || 'Gagal menambah ke keranjang';
      showToast(toast, 'error', message);
      buyerCaptureException(
        axiosError?.response?.data || { message },
        (scope) => {
          scope.setTag('cart-recommendation-add', `productId ${product.id}`);
          scope.setLevel('error');
        }
      );
    } finally {
      setAddingId(null);
    }
  };

  if (isError || visibleRecommendations.length === 0) return null;

  const renderCard = (item: CartRecommendationItem) => (
    <CartRecommendationCard
      key={item.recommendation.id}
      item={item}
      displayContext={displayContext}
      isAdding={addingId === item.recommendation.id}
      onAdd={handleAdd}
      onSelect={handleSelect}
    />
  );

  return (
    <Box
      ref={containerRef}
      bg="#FAFAFA"
      px={isCartPage ? '4' : { base: '4', md: '6' }}
      py={isCartPage ? '6' : { base: '4', md: '6' }}
      borderRadius="8px"
      w="100%"
      data-testid="cart-recommendation-module"
    >
      <Box mb="4" px={isCartPage ? '2' : '0'}>
        <Text
          fontSize="20px"
          fontWeight="500"
          lineHeight="24px"
          color="#373F41"
        >
          {RECOMMENDATION_TITLE_POPUP}
        </Text>
      </Box>

      {isCartPage ? (
        <Box
          display="grid"
          gridTemplateColumns={{
            base: 'repeat(2, 144px)',
            md: 'repeat(4, 144px)',
            lg: 'repeat(5, 144px)',
          }}
          columnGap={{ base: '16px', lg: '23px' }}
          rowGap={{ base: '24px', lg: '32px' }}
          overflowX={{ base: 'auto', lg: 'visible' }}
          pb={{ base: '1', lg: '0' }}
          sx={{
            scrollbarWidth: 'none',
            '::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {visibleRecommendations.map(renderCard)}
        </Box>
      ) : isMobile ? (
        <SimpleGrid columns={2} spacing="4">
          {visibleRecommendations.map(renderCard)}
        </SimpleGrid>
      ) : (
        <SimpleGrid columns={4} spacing="4">
          {visibleRecommendations.map(renderCard)}
        </SimpleGrid>
      )}
    </Box>
  );
}
